/**
 * Supported content asset types per social platform.
 * Keys match platform values used elsewhere (e.g. campaign wizard, settings).
 *
 * @type {Record<string, string[]>}
 */
export const platformAssets = {
  facebook: ["post", "reel", "video", "image", "story"],
  instagram: ["post", "reel", "story", "carousel"],
  linkedin: ["post", "article", "video", "image", "carousel", "newsletter"],
  twitter: ["post", "video", "image", "poll"], // "post" = tweet/text update on X
};

/**
 * One-line meaning of each asset type (for prompts / AI — not UI labels).
 * @type {Record<string, string>}
 */
export const platformAssetDescriptions = {
  post: "Standard feed update: caption/text with optional single or mixed media.",
  reel: "Short-form vertical video for Reels tab (typically 15–90 seconds, mobile-first).",
  video: "Longer-form or standard video post for the main feed (not Reels-specific).",
  image: "Single static image post with caption; no multi-slide or album layout.",
  story: "Ephemeral 24-hour vertical story (full-screen, tap-through format).",
  album: "Multi-photo post grouped as one Facebook album/carousel in feed.",
  carousel: "Multi-slide swipeable post (several images or cards in one publish unit).",
  article: "Long-form LinkedIn article (headline, body, cover image — not a short feed post).",
  newsletter: "LinkedIn newsletter edition sent to subscribers (recurring long-form series).",
  poll: "X post with built-in poll options and vote duration (text + choices, not media-first).",
};

/**
 * Bootstrap icon class per asset type (for UI cards).
 * @type {Record<string, string>}
 */
export const platformAssetIcons = {
  post: "bi-card-text",
  reel: "bi-phone",
  video: "bi-play-btn",
  image: "bi-image",
  story: "bi-clock-history",
  album: "bi-images",
  carousel: "bi-collection",
  article: "bi-journal-richtext",
  newsletter: "bi-envelope-paper",
  poll: "bi-ui-radios",
};

/** @param {string} assetType */
export function getAssetIcon(assetType) {
  return platformAssetIcons[assetType] ?? "bi-file-earmark";
}

/** @param {string} assetType */
export function formatAssetLabel(assetType) {
  if (!assetType) return "";
  return assetType.charAt(0).toUpperCase() + assetType.slice(1);
}

/** @param {string | null | undefined} platform */
export function getAssetsForPlatform(platform) {
  if (!platform) return [];
  const key = String(platform).toLowerCase();
  return platformAssets[key] ?? [];
}
